/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Board Variant Picker 2026
 * -------------------------
 * Handles variant selection for Exocet flat products
 * (each size/construction combo = separate Shopify product).
 *
 * All changes (construction, pack, volume, length, simple, boardbag) swap
 * in place via the shared helper window.ExocetVariantSwap (variant-swap.js):
 * crossfades gallery, updates info panel, pushes URL -- no page reload.
 */
(function () {
  "use strict";

  function BoardVariantPicker(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.mode = el.dataset.bvpMode;
    this.data = parseData(this.sectionId);
    this.section = el.closest(".product-hero-2026");
    this.pendingSelection = null;

    if (!this.section || !this.data) return;

    var self = this;
    el.addEventListener("click", function (e) {
      var btn = e.target.closest("button");
      if (!btn) return;

      if (btn.hasAttribute("data-bvp-construction")) {
        self.onConstruction(btn);
      } else if (btn.hasAttribute("data-bvp-pack")) {
        self.onPack(btn);
      } else if (btn.hasAttribute("data-bvp-volume")) {
        self.onVolume(btn);
      } else if (btn.hasAttribute("data-bvp-simple")) {
        self.onSimple(btn);
      } else if (btn.hasAttribute("data-bvp-length")) {
        if (self.data.mode === "boardbag") {
          self.onLength(btn);
        } else {
          self.onBoardLength(btn);
        }
      } else if (btn.hasAttribute("data-bvp-width")) {
        self.onWidth(btn);
      }
    });

    this.refreshAvailability();
  }

  function parseData(sectionId) {
    var el = document.getElementById("bvp-data-" + sectionId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------------------------
  // Construction: AJAX swap
  // ------------------------------------------------------------------

  BoardVariantPicker.prototype.onConstruction = function (btn) {
    var target = btn.getAttribute("data-bvp-construction");
    if (this.data.mode === "rig") {
      if (this.isAxisCurrentOrPending("construction", target)) return;
      var pack = this.resolveRigPack(target, this.currentSizeValue(), this.currentPackValue());
      var rigVariant = this.findRigVariant(target, pack, this.currentSizeValue());
      if (!rigVariant) return;

      this.setSelected("construction", target);
      this.setSelected("pack", pack);
      this.swap(rigVariant, target, pack);
      return;
    }

    if (this.isAxisCurrentOrPending("construction", target)) return;

    var variant = this.findVariant(target, this.currentSizeValue());
    if (!variant) return;

    this.setSelected("construction", target);
    this.swap(variant, target);
  };

  BoardVariantPicker.prototype.onPack = function (btn) {
    if (this.data.mode !== "rig") return;

    var target = btn.getAttribute("data-bvp-pack");
    if (this.isAxisCurrentOrPending("pack", target)) return;

    var variant = this.findRigVariant(this.currentConstructionValue(), target, this.currentSizeValue());
    if (!variant) return;

    this.setSelected("pack", target);
    this.swap(variant, this.currentConstructionValue(), target);
  };

  // Delegate the DOM swap to the shared helper (variant-swap.js), then apply
  // board-specific state. `construction`/`pack` cover construction/rig changes;
  // `sizeState` carries the new size-axis value(s) for size changes. For a
  // size-only swap, pass the current construction/mast so it is preserved.
  BoardVariantPicker.prototype.swap = function (variant, construction, pack, sizeState) {
    var self = this;
    if (!window.ExocetVariantSwap) {
      window.location.href = variant.url;
      return;
    }
    var pendingSelection = this.createPendingSelection(construction, pack, sizeState);
    this.pendingSelection = pendingSelection;
    return window.ExocetVariantSwap.swap({
      section: this.section,
      sectionId: this.sectionId,
      url: variant.url,
      variant: variant,
      onAfterSwap: function () {
        if (self.data.mode === "rig") {
          if (construction != null) self.data.currentMast = construction;
          if (typeof pack === "string") self.data.currentPack = pack;
        } else if (construction != null) {
          self.data.currentConstruction = construction;
        }
        if (sizeState) {
          if (typeof sizeState.currentVolume === "string") self.data.currentVolume = sizeState.currentVolume;
          if (typeof sizeState.currentLength === "string") self.data.currentLength = sizeState.currentLength;
          if (typeof sizeState.currentWidth === "string") self.data.currentWidth = sizeState.currentWidth;
          if (typeof sizeState.currentValue === "string") self.data.currentValue = sizeState.currentValue;
        }
        self.data.currentUrl = variant.url;
        if (self.pendingSelection === pendingSelection) self.pendingSelection = null;
        self.refreshAvailability();
      },
    });
  };

  // ------------------------------------------------------------------
  // Volume / length / simple: AJAX swap (was page navigation)
  // ------------------------------------------------------------------

  BoardVariantPicker.prototype.onVolume = function (btn) {
    var target = btn.getAttribute("data-bvp-volume");
    if (this.isAxisCurrentOrPending("volume", target)) return;

    if (this.data.mode === "rig") {
      var pack = this.resolveRigPack(this.currentConstructionValue(), target, this.currentPackValue());
      var rigVariant = this.findRigVariant(this.currentConstructionValue(), pack, target);
      if (!rigVariant) return;
      this.setSelected("volume", target);
      this.setSelected("pack", pack);
      this.swap(rigVariant, this.currentConstructionValue(), pack, { currentVolume: target });
      return;
    }

    var variant = this.findVariant(this.currentConstructionValue(), target);
    if (!variant) return;
    this.setSelected("volume", target);
    this.swap(variant, this.currentConstructionValue(), undefined, { currentVolume: target });
  };

  BoardVariantPicker.prototype.onBoardLength = function (btn) {
    var target = btn.getAttribute("data-bvp-length");
    if (this.isAxisCurrentOrPending("length", target)) return;

    var variant = this.findVariant(this.currentConstructionValue(), target);
    if (!variant) return;
    this.setSelected("length", target);
    this.swap(variant, this.currentConstructionValue(), undefined, { currentLength: target });
  };

  BoardVariantPicker.prototype.onSimple = function (btn) {
    var target = btn.getAttribute("data-bvp-simple");
    if (this.isAxisCurrentOrPending("simple", target)) return;

    var variant = this.data.variants.find(function (v) {
      return v.value === target;
    });
    if (!variant) return;
    this.setSelected("simple", target);
    this.swap(variant, undefined, undefined, { currentValue: target });
  };

  // ------------------------------------------------------------------
  // Boardbag (Length x Width): AJAX swap, both axes dependent. Derive both
  // axes from the resolved variant (a length pick may fall back to a
  // different width).
  // ------------------------------------------------------------------

  BoardVariantPicker.prototype.onLength = function (btn) {
    var target = btn.getAttribute("data-bvp-length");
    if (this.isAxisCurrentOrPending("length", target)) return;

    // Keep the current width if that L x W combo exists; otherwise fall back
    // to the first available (then any) width for the new length.
    var variant =
      this.findBoardbagVariant(target, this.currentWidthValue()) ||
      this.data.variants.find(function (v) {
        return v.length === target && v.available;
      }) ||
      this.data.variants.find(function (v) {
        return v.length === target;
      });

    if (!variant) return;
    this.setSelected("length", variant.length);
    this.setSelected("width", variant.width);
    this.swap(variant, undefined, undefined, { currentLength: variant.length, currentWidth: variant.width });
  };

  BoardVariantPicker.prototype.onWidth = function (btn) {
    var target = btn.getAttribute("data-bvp-width");
    if (this.isAxisCurrentOrPending("width", target)) return;

    var variant =
      this.findBoardbagVariant(this.currentLengthValue(), target) ||
      this.data.variants.find(function (v) {
        return v.width === target && v.available;
      }) ||
      this.data.variants.find(function (v) {
        return v.width === target;
      });

    if (!variant) return;
    this.setSelected("length", variant.length);
    this.setSelected("width", variant.width);
    this.swap(variant, undefined, undefined, { currentLength: variant.length, currentWidth: variant.width });
  };

  BoardVariantPicker.prototype.findBoardbagVariant = function (length, width) {
    return this.data.variants.find(function (v) {
      return v.length === length && v.width === width;
    });
  };

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  BoardVariantPicker.prototype.isAxisCurrentOrPending = function (axis, target) {
    if (this.pendingSelection) return this.pendingSelection[axis] === target;

    if (axis === "construction") return target === this.currentConstructionValue();
    if (axis === "pack") return target === this.currentPackValue();
    if (axis === "volume") return target === this.currentVolumeValue();
    if (axis === "length") return target === this.currentLengthValue();
    if (axis === "simple") return target === this.data.currentValue;
    if (axis === "width") return target === this.currentWidthValue();
    return false;
  };

  BoardVariantPicker.prototype.createPendingSelection = function (construction, pack, sizeState) {
    var pending = this.pendingSelection
      ? {
          construction: this.pendingSelection.construction,
          pack: this.pendingSelection.pack,
          volume: this.pendingSelection.volume,
          length: this.pendingSelection.length,
          simple: this.pendingSelection.simple,
          width: this.pendingSelection.width,
        }
      : {
          construction: this.data.mode === "rig" ? this.data.currentMast : this.data.currentConstruction,
          pack: this.data.currentPack,
          volume: this.data.currentVolume,
          length: this.data.currentLength,
          simple: this.data.currentValue,
          width: this.data.currentWidth,
        };

    if (construction != null) pending.construction = construction;
    if (typeof pack === "string") pending.pack = pack;
    if (sizeState) {
      if (typeof sizeState.currentVolume === "string") pending.volume = sizeState.currentVolume;
      if (typeof sizeState.currentLength === "string") pending.length = sizeState.currentLength;
      if (typeof sizeState.currentWidth === "string") pending.width = sizeState.currentWidth;
      if (typeof sizeState.currentValue === "string") pending.simple = sizeState.currentValue;
    }

    return pending;
  };

  BoardVariantPicker.prototype.usesLengthAxis = function () {
    return this.data.sizeAxis === "length";
  };

  BoardVariantPicker.prototype.currentSizeValue = function () {
    return this.usesLengthAxis() ? this.currentLengthValue() : this.currentVolumeValue();
  };

  BoardVariantPicker.prototype.currentConstructionValue = function () {
    if (this.pendingSelection) return this.pendingSelection.construction;
    return this.data.mode === "rig" ? this.data.currentMast : this.data.currentConstruction;
  };

  BoardVariantPicker.prototype.currentPackValue = function () {
    return this.pendingSelection ? this.pendingSelection.pack : this.data.currentPack;
  };

  BoardVariantPicker.prototype.currentVolumeValue = function () {
    return this.pendingSelection ? this.pendingSelection.volume : this.data.currentVolume;
  };

  BoardVariantPicker.prototype.currentLengthValue = function () {
    return this.pendingSelection ? this.pendingSelection.length : this.data.currentLength;
  };

  BoardVariantPicker.prototype.currentWidthValue = function () {
    return this.pendingSelection ? this.pendingSelection.width : this.data.currentWidth;
  };

  BoardVariantPicker.prototype.findVariant = function (construction, size) {
    var self = this;
    return this.data.variants.find(function (v) {
      var sizeMatch = self.usesLengthAxis() ? v.length === size : v.volume === size;
      return v.construction === construction && sizeMatch;
    });
  };

  BoardVariantPicker.prototype.isCompactRigSize = function (size) {
    return (
      this.data.mode === "rig" && Array.isArray(this.data.compactSizes) && this.data.compactSizes.indexOf(size) !== -1
    );
  };

  BoardVariantPicker.prototype.resolveRigPack = function (mast, size, preferredPack) {
    if (mast !== "EPX" || !this.isCompactRigSize(size)) return "Standard";
    var pack = preferredPack || "Standard";
    if (this.findRigVariant(mast, pack, size)) return pack;
    return "Standard";
  };

  BoardVariantPicker.prototype.findRigVariant = function (mast, pack, size) {
    return this.data.variants.find(function (v) {
      return v.mast === mast && v.pack === pack && v.volume === size;
    });
  };

  // Mark size/construction pills whose matching sibling product is sold out
  // (or doesn't exist) with the `unavailable` state, relative to the current
  // selection. Subtle visual cue so shoppers can see sold-out options.
  BoardVariantPicker.prototype.refreshRigPackSelector = function () {
    var fieldset = this.el.querySelector("[data-bvp-pack-fieldset]");
    if (!fieldset) return;

    var show = this.data.currentMast === "EPX" && this.isCompactRigSize(this.currentSizeValue());
    fieldset.classList.toggle("hidden", !show);
  };

  BoardVariantPicker.prototype.refreshAvailability = function () {
    if (!this.data || !this.data.variants) return;
    var self = this;

    if (this.data.mode === "rig") {
      this.refreshRigPackSelector();

      this.el.querySelectorAll("[data-bvp-volume]").forEach(function (btn) {
        var size = btn.getAttribute("data-bvp-volume");
        var pack = self.resolveRigPack(self.data.currentMast, size, self.data.currentPack);
        var v = self.findRigVariant(self.data.currentMast, pack, size);
        btn.classList.toggle("unavailable", !(v && v.available));
      });

      this.el.querySelectorAll("[data-bvp-construction]").forEach(function (btn) {
        var mast = btn.getAttribute("data-bvp-construction");
        var pack = self.resolveRigPack(mast, self.currentSizeValue(), self.data.currentPack);
        var v = self.findRigVariant(mast, pack, self.currentSizeValue());
        btn.classList.toggle("unavailable", !(v && v.available));
      });

      this.el.querySelectorAll("[data-bvp-pack]").forEach(function (btn) {
        var pack = btn.getAttribute("data-bvp-pack");
        var v = self.findRigVariant(self.data.currentMast, pack, self.currentSizeValue());
        btn.classList.toggle("unavailable", !(v && v.available));
      });

      return;
    }

    this.el.querySelectorAll("[data-bvp-simple]").forEach(function (btn) {
      var v = self.data.variants.find(function (x) {
        return x.value === btn.getAttribute("data-bvp-simple");
      });
      btn.classList.toggle("unavailable", !(v && v.available));
    });

    this.el.querySelectorAll("[data-bvp-volume]").forEach(function (btn) {
      var v = self.findVariant(self.data.currentConstruction, btn.getAttribute("data-bvp-volume"));
      btn.classList.toggle("unavailable", !(v && v.available));
    });
    if (this.usesLengthAxis()) {
      this.el.querySelectorAll("[data-bvp-length]").forEach(function (btn) {
        var v = self.findVariant(self.data.currentConstruction, btn.getAttribute("data-bvp-length"));
        btn.classList.toggle("unavailable", !(v && v.available));
      });
    }
    this.el.querySelectorAll("[data-bvp-construction]").forEach(function (btn) {
      var v = self.findVariant(btn.getAttribute("data-bvp-construction"), self.currentSizeValue());
      btn.classList.toggle("unavailable", !(v && v.available));
    });
    if (this.data.mode === "boardbag") {
      // Boardbag axes: widths re-mark against the current length; lengths mark
      // as unavailable only when fully sold out across every width.
      this.el.querySelectorAll("[data-bvp-width]").forEach(function (btn) {
        var v = self.findBoardbagVariant(self.data.currentLength, btn.getAttribute("data-bvp-width"));
        btn.classList.toggle("unavailable", !(v && v.available));
      });
      this.el.querySelectorAll("[data-bvp-length]").forEach(function (btn) {
        var len = btn.getAttribute("data-bvp-length");
        var any = self.data.variants.some(function (v) {
          return v.length === len && v.available;
        });
        btn.classList.toggle("unavailable", !any);
      });
    }
  };

  BoardVariantPicker.prototype.setSelected = function (type, value) {
    var attr = "data-bvp-" + type;
    this.el.querySelectorAll("[" + attr + "]").forEach(function (btn) {
      var match = btn.getAttribute(attr) === value;
      btn.classList.toggle("selected", match);
      btn.setAttribute("aria-pressed", String(match));
    });

    var display = this.el.querySelector("[data-bvp-" + type + "-value]");
    if (display) {
      if (type === "length" && typeof value === "string" && value.indexOf(" Bamboo") !== -1) {
        display.textContent = value.replace(" Bamboo", "");
      } else if (type === "volume" && this.data.mode === "rig") {
        display.innerHTML = value + "&nbsp;m&sup2;";
      } else if (type === "pack") {
        display.textContent = value;
      } else {
        display.textContent = value;
      }
    }
  };

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll(".bvp").forEach(function (el) {
      new BoardVariantPicker(el);
    });
  });
})();
